import { yupResolver } from '@hookform/resolvers/yup';
import Close from '@mui/icons-material/Close';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { ADMIN_PASSWORD_PATTERN } from '@/common/constants/pattern';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import AppPasswordTextField from '@/common/components/form-inputs/AppPasswordTextField';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useChangeUserPasswordMutation } from '@/api/user/userApiSlice';
import { useParams } from 'react-router-dom';
interface ChangePasswordModalProps {}

const ChangePasswordModal = (props: ChangePasswordModalProps) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [changeUserPassword] = useChangeUserPasswordMutation();
	const { id } = useParams();

	const defaultValues: DefaultValues<any> = {};

	const formSchema = Yup.object().shape({
		newPassword: Yup.string()
			.required(t('errorMessage.required'))
			.matches(ADMIN_PASSWORD_PATTERN, t('errorMessage.passwordMin8')),
		password: Yup.string()
			.required(t('errorMessage.required'))
			.oneOf([Yup.ref('newPassword')], t('errorMessage.unlikePassword')),
	});

	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const handleClose = () => {
		methods.reset({});
		setOpen(false);
	};

	const onSubmit: SubmitHandler<any> = async (data) => {
		const response = await changeUserPassword({ ...data, userId: id });
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.changePasswordSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
		}
	};

	return (
		<>
			<Button
				endIcon={<LockOutlinedIcon />}
				onClick={() => {
					setOpen(true);
				}}
				sx={{ marginRight: '24px' }}
				variant='contained'
			>
				{t('button.editPassword')}
			</Button>
			<Dialog
				open={open}
				onClose={handleClose}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='xs'
				fullWidth
				onKeyDown={(e) => {
					if (e.key === 'Tab') {
						e.stopPropagation();
					}
				}}
			>
				<DialogTitle variant='h5'>{t('common.changePassword')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(onSubmit)}>
							<Grid container spacing={2} marginTop={0} marginBottom={2}>
								<Grid item xs={12}>
									<AppPasswordTextField
										control={methods.control}
										name='newPassword'
										label={t('user.newPassword')}
									/>
								</Grid>
								<Grid item xs={12}>
									<AppPasswordTextField
										control={methods.control}
										name='password'
										label={t('user.confirmNewPassword')}
									/>
								</Grid>
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button
						type='submit'
						variant='contained'
						onClick={methods.handleSubmit(onSubmit)}
					>
						{t('button.save')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default ChangePasswordModal;
